import React, { useState, useRef, useEffect } from "react";
import { Box, Portal } from "@chakra-ui/react";
import { HoverInfoTooltip } from "./HoverInfoTooltip";

export interface TourGuideHoverTooltipProps {
  title: string;
  description: string;
  targetRef?: React.RefObject<HTMLElement>;
  targetSelector?: string;
  position?: "top" | "bottom" | "left" | "right" | "center";
  offset?: { x: number; y: number };
  enabled?: boolean;
  isVisible?: boolean; // Controlled by parent
}

export const TourGuideHoverTooltip: React.FC<TourGuideHoverTooltipProps> = ({
  title,
  description,
  targetRef,
  targetSelector,
  position = "right",
  offset = { x: 20, y: 0 },
  enabled = true,
  isVisible: controlledIsVisible = true, // Default to true if not provided (for backward compatibility)
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Calculate position for tooltip
  const updatePosition = () => {
    if (!enabled || !controlledIsVisible) return;

    let element: HTMLElement | null = null;

    if (targetRef?.current) {
      element = targetRef.current;
    } else if (targetSelector) {
      element = document.querySelector(targetSelector) as HTMLElement;
    }

    if (!element) return;

    const rect = element.getBoundingClientRect();
    // For fixed positioning, use getBoundingClientRect directly (no scroll offset needed)
    let tooltipTop = rect.top;
    let tooltipLeft = rect.left;

    switch (position) {
      case "right":
        tooltipLeft = rect.right + (offset.x || 12); // Smaller default offset for hover
        tooltipTop = rect.top + (offset.y || 0);
        break;
      case "left":
        tooltipLeft = rect.left - offset.x - 320; // Approximate tooltip width
        tooltipTop = rect.top + offset.y;
        break;
      case "top":
        tooltipLeft = rect.left + offset.x;
        tooltipTop = rect.top - offset.y - 100; // Approximate tooltip height
        break;
      case "bottom":
        tooltipLeft = rect.left + offset.x;
        tooltipTop = rect.bottom + offset.y;
        break;
      case "center":
        tooltipLeft = window.innerWidth / 2 - 160;
        tooltipTop = window.innerHeight / 2 - 50;
        break;
    }

    // Ensure tooltip stays within viewport
    // Smaller width for hover tooltip (more compact)
    const tooltipWidth = 320;
    const tooltipHeight = 100; // Approximate height
    if (tooltipLeft + tooltipWidth > window.innerWidth) {
      tooltipLeft = window.innerWidth - tooltipWidth - 20;
    }
    if (tooltipLeft < 0) {
      tooltipLeft = 20;
    }
    if (tooltipTop + tooltipHeight > window.innerHeight) {
      tooltipTop = window.innerHeight - tooltipHeight - 20;
    }
    if (tooltipTop < 0) {
      tooltipTop = 20;
    }

    setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
  };

  useEffect(() => {
    if (controlledIsVisible && enabled) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [
    controlledIsVisible,
    enabled,
    targetRef,
    targetSelector,
    position,
    offset,
  ]);

  if (!enabled || !controlledIsVisible || !tooltipPosition) {
    return null;
  }

  return (
    <Portal>
      <Box
        position="fixed"
        top={`${tooltipPosition.top}px`}
        left={`${tooltipPosition.left}px`}
        zIndex={10000}
        pointerEvents="none" // Allow mouse events to pass through to underlying elements
      >
        <HoverInfoTooltip
          title={title}
          description={description}
          position={position}
        />
      </Box>
    </Portal>
  );
};

// Hook to attach hover handlers to an element
export const useTourGuideHover = (
  stepId: string,
  config: {
    title: string;
    description: string;
    position?: "top" | "bottom" | "left" | "right" | "center";
    offset?: { x: number; y: number };
    enabled?: boolean;
  }
) => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (!config.enabled) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return {
    isHovered,
    hoverProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
  };
};
